/** CSS contract: css-library/components/01-leaves/date-picker.css. */
import type { Ref } from 'react';
import { usePopoverEntered } from '../lib/usePopoverEntered';
import { ButtonControl } from './ButtonControl';
import { useModalPopover, usePopoverContainer } from '../lib/modalContext';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FocusEvent, InputHTMLAttributes, KeyboardEvent } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { cx } from '../lib/cx';
import { mergeRefs } from '../lib/merge-refs';
import { placePopover } from '../lib/popoverPlace';
import { Icon } from './Icon';
import { Select } from './Select';

const DEFAULT_WEEKDAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const DEFAULT_MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const DEFAULT_PLACEHOLDER = 'TT.MM.JJJJ';

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const NESTED_SELECT_POPOVER_SELECTOR =
  '.select-popover:not(.filter-menu-list):not(.filter-menu-flyout)';

function parseIso(s: string): Date | null {
  const m = ISO_RE.exec(s || '');
  if (!m) return null;
  const y = +m[1];
  const mo = +m[2];
  const day = +m[3];
  const d = new Date(y, mo - 1, day);
  if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== day) return null;
  return d;
}

function parseTyped(s: string): Date | null {
  const t = s.trim();
  let y = 0;
  let mo = 0;
  let day = 0;
  let m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(t);
  if (m) {
    day = +m[1]; mo = +m[2]; y = +m[3];
  } else if ((m = /^(\d{2})(\d{2})(\d{4})$/.exec(t))) {
    day = +m[1]; mo = +m[2]; y = +m[3];
  } else if ((m = ISO_RE.exec(t))) {
    y = +m[1]; mo = +m[2]; day = +m[3];
  } else {
    return null;
  }
  if (mo < 1 || mo > 12 || day < 1) return null;
  const lastDay = new Date(y, mo, 0).getDate();
  return new Date(y, mo - 1, Math.min(day, lastDay));
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function toDisplay(iso: string): string {
  const d = parseIso(iso);
  if (!d) return '';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

function gridCellsFor(year: number, month: number): Array<{ date: Date; inMonth: boolean }> {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;

  const start = new Date(year, month, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { date, inMonth: date.getMonth() === month };
  });
}

function monthIndex(date: Date): number {
  return date.getFullYear() * 12 + date.getMonth();
}

function sameDay(a: Date | null, b: Date | null): boolean {
  return (
    !!a && !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function clampDate(d: Date, min: Date | null, max: Date | null): Date {
  if (min && d < min) return min;
  if (max && d > max) return max;
  return d;
}

export interface DatePickerProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'type' | 'value' | 'defaultValue' | 'min' | 'max' | 'onChange' | 'name'
  > {
  value: string;
  onChange: (iso: string) => void;

  min?: string;
  max?: string;

  prevMonthLabel?: string;
  nextMonthLabel?: string;

  pastYears?: number;
}

export function DatePicker(
  { ref, value,
    onChange,
    min,
    max,
    prevMonthLabel,
    nextMonthLabel,
    pastYears = 0,
    placeholder = DEFAULT_PLACEHOLDER,
    disabled,
    className,
    onFocus,
    onBlur,
    onKeyDown,
    ...rest
  }: DatePickerProps & { ref?: Ref<HTMLInputElement> },
) {
  const popoverContainer = usePopoverContainer();
  const selected = parseIso(value);
  const today = new Date();
  let minDate = min ? parseIso(min) : null;
  let maxDate = max ? parseIso(max) : null;
  if (minDate && maxDate && minDate > maxDate) {
    minDate = null;
    maxDate = null;
  }

  const base = selected ?? clampDate(today, minDate, maxDate);
  const [open, setOpen] = useState(false);
  useModalPopover(open, () => setOpen(false));
  const entered = usePopoverEntered(open);
  const [draft, setDraft] = useState<string | null>(null);
  const [view, setView] = useState(monthIndex(base));
  const viewYear = Math.floor(view / 12);
  const viewMonth = view - viewYear * 12;
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight?: number }>({
    top: 0,
    left: 0,
  });
  const pickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const display = selected ? toDisplay(toIso(selected)) : '';
  const emit = (iso: string) => {
    if (!disabled) onChange(iso);
  };
  const todayYear = today.getFullYear();
  const baseYear = base.getFullYear();
  const rangeLow = Math.min(todayYear - pastYears, baseYear);
  const rangeHigh = Math.max(todayYear + 5, baseYear);
  const windowMinYear = minDate?.getFullYear();
  const windowMaxYear = maxDate?.getFullYear();
  const yearOptions: Array<{ value: string; label: string }> = [];
  for (let y = rangeLow; y <= rangeHigh; y++) {
    const inWindow =
      (windowMinYear == null || y >= windowMinYear) && (windowMaxYear == null || y <= windowMaxYear);
    if (inWindow || y === baseYear) yearOptions.push({ value: String(y), label: String(y) });
  }

  if (pastYears > 0) yearOptions.reverse();
  const reposition = useCallback(() => {
    const input = inputRef.current;
    const pop = popoverRef.current;
    if (!input || !pop) return;
    setPos(
      placePopover(input.getBoundingClientRect(), pop.getBoundingClientRect().width, pop.scrollHeight + 2),
    );
  }, []);
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: globalThis.MouseEvent) => {
      const t = e.target as HTMLElement;
      if (pickerRef.current?.contains(t) || popoverRef.current?.contains(t)) return;
      if (t.closest?.(NESTED_SELECT_POPOVER_SELECTOR)) return;
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector(NESTED_SELECT_POPOVER_SELECTOR)) return;
      if (e.target === inputRef.current) return;
      e.stopPropagation();
      setOpen(false);
    };
    const onScroll = (e: Event) => {
      const t = e.target as HTMLElement;
      if (popoverRef.current?.contains(t)) return;
      if (t.closest?.(NESTED_SELECT_POPOVER_SELECTOR)) return;
      setOpen(false);
    };
    const onResize = () => flushSync(() => reposition());
    document.addEventListener('mousedown', onOutside, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onOutside, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, reposition]);
  function commitDraft() {
    if (draft === null) return;
    setDraft(null);
    const d = parseTyped(draft);
    if (!d) return;
    if ((minDate && d < minDate) || (maxDate && d > maxDate)) return;
    emit(toIso(d));
    setView(monthIndex(d));
  }

  function handleFocus(e: FocusEvent<HTMLInputElement>) {
    onFocus?.(e);
    if (e.defaultPrevented) return;
    e.currentTarget.select();
    setOpen(true);
  }

  function handleTyping(raw: string) {
    if (raw.includes('-')) {
      setDraft(raw);
      return;
    }

    const prev = draft ?? display;
    const typing = raw.length > prev.length;
    let d = raw.replace(/\D/g, '').slice(0, 8);
    const seg = (segMax: number, pad: number) => {
      if (!d.length) return '';
      if (+d[0] > pad) {
        const out = '0' + d[0];
        d = d.slice(1);
        return out;
      }
      if (d.length >= 2) {
        const two = +d.slice(0, 2);
        if (two >= 1 && two <= segMax) {
          const out = d.slice(0, 2);
          d = d.slice(2);
          return out;
        }
        const out = d[0];
        d = '';
        return out;
      }
      const out = d[0];
      d = '';
      return out;
    };
    const day = seg(31, 3);
    const mon = seg(12, 1);
    const year = d.slice(0, 4);
    let masked = day;
    if (day.length === 2 && (mon || typing)) masked += '.';
    masked += mon;
    if (mon.length === 2 && (year || typing)) masked += '.';
    masked += year;
    setDraft(masked);
    if (year.length === 4) {
      const parsed = parseTyped(masked);
      if (parsed && !((minDate && parsed < minDate) || (maxDate && parsed > maxDate))) {
        emit(toIso(parsed));
        setView(monthIndex(parsed));
        setDraft(null);
      }
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDraft();
      setOpen(false);
    }
    if (e.key === 'Escape' && (draft !== null || open)) {
      e.stopPropagation();
      setDraft(null);
      setOpen(false);
    }
  }

  function handleBlur(e: FocusEvent<HTMLInputElement>) {
    onBlur?.(e);
    if (e.defaultPrevented) return;
    commitDraft();
    const next = e.relatedTarget;
    if (!(next instanceof HTMLElement)) return;
    if (pickerRef.current?.contains(next) || popoverRef.current?.contains(next)) return;
    if (next.closest(NESTED_SELECT_POPOVER_SELECTOR)) return;
    setOpen(false);
  }

  return (
    <div ref={pickerRef} className={cx('date-picker', open && 'is-open', className)}>
      <input
        ref={mergeRefs(inputRef, ref)}
        type="text"
        className="date-picker-input"
        value={draft ?? display}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={handleFocus}
        onChange={(e) => handleTyping(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        {...rest}
      />

      <ButtonControl
        type="button"
        className="date-picker-toggle"
        aria-hidden="true"
        tabIndex={-1}
        disabled={disabled}
      >
        <Icon name="i-calendar" />
      </ButtonControl>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className={cx('date-picker-popover', entered && 'is-open')}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              maxHeight: pos.maxHeight,
              overflowY: 'auto',
            }}
          >
            <div className="date-picker-header">
              <ButtonControl
                type="button"
                className="date-picker-nav"
                title={prevMonthLabel}
                aria-label={prevMonthLabel}
                onClick={() => setView((month) => month - 1)}
              >
                <Icon name="i-arrow-left" />
              </ButtonControl>
              <div className="date-picker-month">
                <span className="date-picker-month-name">{DEFAULT_MONTH_NAMES[viewMonth]}</span>
                <Select
                  compact
                  options={yearOptions}
                  value={String(viewYear)}
                  placeholder={String(viewYear)}
                  onChange={(v) => {
                    const y = parseInt(v, 10);
                    if (!Number.isNaN(y)) setView(y * 12 + viewMonth);
                  }}
                />
              </div>
              <ButtonControl
                type="button"
                className="date-picker-nav"
                title={nextMonthLabel}
                aria-label={nextMonthLabel}
                onClick={() => setView((month) => month + 1)}
              >
                <Icon name="i-arrow-right" />
              </ButtonControl>
            </div>
            <div className="date-picker-weekdays">
              {DEFAULT_WEEKDAY_NAMES.map((wd) => (
                <div key={wd}>{wd}</div>
              ))}
            </div>
            <div className="date-picker-days">
              {gridCellsFor(viewYear, viewMonth).map((cell, i) => {
                const outOfWindow =
                  (!!minDate && cell.date < minDate) || (!!maxDate && cell.date > maxDate);
                return (
                  <ButtonControl
                    key={i}
                    type="button"
                    className={cx(
                      'day',
                      !cell.inMonth && 'is-other-month',
                      sameDay(cell.date, today) && 'is-today',
                      sameDay(cell.date, selected) && 'is-selected',
                    )}
                    disabled={outOfWindow}
                    onClick={() => {
                      emit(toIso(cell.date));
                      setOpen(false);
                    }}
                  >
                    {cell.date.getDate()}
                  </ButtonControl>
                );
              })}
            </div>
          </div>,
          popoverContainer ?? document.body,
        )}
    </div>
  );
}
